import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SearchOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { dt, paginationWrapperSx } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  browseCatalogModels,
  getCatalogCategories,
  getCatalogManufacturers,
  getCatalogTaperTypes,
} from "../machineCatalogSlice";
import { CatalogBrowseQuery, CatalogManufacturer, CatalogManufacturerSummary, CatalogModelListItem, MachineCatalogState, TaperType } from "../models/models";
import CatalogCategoryTabs from "./CatalogCategoryTabs";
import CatalogModelCard from "./CatalogModelCard";
import OemStar from "./OemStar";
import MachineDialog from "./MachineDialog";
import { getReferenceLabel } from "../../../shared/utils/machineReferenceLabels";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { getUiLocale } from "../../../shared/utils/relativeTime";

// =====================================================================
//  FilterItem (same pattern as Machinery.tsx)
// =====================================================================

interface FilterItemProps {
  label: string;
  count: number;
  isSelected: boolean;
  statusColor?: string;
  indent?: boolean;
  onClick: () => void;
}

const FilterItem = ({ label, count, isSelected, statusColor, indent, onClick }: FilterItemProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "9px 10px",
      paddingLeft: indent ? "14px" : "10px",
      marginLeft: indent ? "8px" : 0,
      borderRadius: dt.radiusSm,
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: isSelected ? (statusColor ? `${statusColor}08` : dt.blueSoft) : "transparent",
      borderLeft: isSelected && statusColor
        ? `3px solid ${statusColor}`
        : isSelected
          ? `3px solid ${dt.blue}`
          : "3px solid transparent",
      "&:hover": {
        backgroundColor: isSelected ? (statusColor ? `${statusColor}10` : dt.blueGlow) : "#F8FAFC",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontFamily: dt.font,
        fontWeight: isSelected ? 600 : 400,
        color: isSelected ? (statusColor || dt.blue) : "#4B5563",
        letterSpacing: "0.1px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
      }}
    >
      {statusColor && (
        <Box
          sx={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: statusColor,
            flexShrink: 0,
            opacity: isSelected ? 1 : 0.5,
            transition: "opacity 0.2s",
          }}
        />
      )}
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "11px",
        fontFamily: dt.fontMono,
        fontWeight: 600,
        color: dt.textMuted,
        backgroundColor: dt.inputBg,
        padding: "2px 8px",
        borderRadius: "10px",
        minWidth: "24px",
        textAlign: "center",
        lineHeight: "16px",
        flexShrink: 0,
      }}
    >
      {count}
    </Box>
  </Box>
);

// =====================================================================
//  CheckboxFilterItem — MIDAS Readiness section (DR-1)
//  Values pulled from Paper artboard v2-1 "NEW Midas readiness section"
// =====================================================================

interface CheckboxFilterItemProps {
  label: string;
  checked: boolean;
  onClick: () => void;
  trailingIcon?: React.ReactNode;
}

const CheckboxFilterItem = ({ label, checked, onClick, trailingIcon }: CheckboxFilterItemProps) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 10px",
      borderRadius: dt.radiusSm,
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      "&:hover": { backgroundColor: "#F8FAFC" },
      // Keyboard focus ring — only when the native input is keyboard-focused
      "&:has(input:focus-visible)": { outline: `2px solid ${dt.blue}`, outlineOffset: "1px" },
    }}
  >
    {/* Visually-hidden native checkbox drives keyboard + screen-reader semantics.
        The label wrapper toggles it; the styled box below is the visual only. */}
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      onChange={onClick}
      aria-label={label}
      sx={{
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        border: 0,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
      }}
    />
    <Box
      aria-hidden="true"
      sx={{
        width: "16px",
        height: "16px",
        borderRadius: "4px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: checked ? dt.blue : "transparent",
        border: checked ? `1.5px solid ${dt.blue}` : "1.5px solid #C9CDD4",
        transition: "all 0.15s ease",
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
          <path d="M20 6L9 17l-5-5" fill="none" stroke="#FFFFFF" strokeWidth="3.5" />
        </svg>
      )}
    </Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        fontSize: "13px",
        fontFamily: dt.font,
        fontWeight: checked ? 600 : 400,
        color: checked ? dt.blue : "#3A3F47",
        minWidth: 0,
      }}
    >
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
      {trailingIcon}
    </Box>
  </Box>
);

// =====================================================================
//  MAIN COMPONENT
// =====================================================================

const PAGE_SIZE_DEFAULT = 20;

const CatalogBrowsePage: React.FC = () => {
  useRedesignSetup();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const {
    browseResults,
    browseTotalCount,
    browseTotalPages,
    browseLoading,
    manufacturers,
    categories,
    taperTypes,
  } = useAppSelector<MachineCatalogState>((state: any) => state.machineCatalog);

  // --- Query state ---
  const [query, setQuery] = useState<CatalogBrowseQuery>({
    page: 1,
    pageSize: PAGE_SIZE_DEFAULT,
  });

  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Add Machine dialog state (from catalog card "+ Add" button) ---
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addDialogModel, setAddDialogModel] = useState<CatalogModelListItem | null>(null);

  const handleAddMachineFromCard = useCallback((model: CatalogModelListItem) => {
    setAddDialogModel(model);
    setShowAddDialog(true);
  }, []);

  // --- Load categories + taper types on mount ---
  useEffect(() => {
    dispatch(getCatalogCategories());
    dispatch(getCatalogTaperTypes());
  }, [dispatch]);

  // --- Load manufacturers when category changes ---
  useEffect(() => {
    dispatch(getCatalogManufacturers({ categoryCode: query.categoryCode }));
  }, [dispatch, query.categoryCode]);

  // --- Fetch browse results when query changes ---
  useEffect(() => {
    dispatch(browseCatalogModels(query));
  }, [dispatch, query]);

  // --- Total count across all categories ---
  const totalCategoryCount = useMemo(() => {
    return categories.reduce((sum, cat) => sum + cat.modelCount, 0);
  }, [categories]);

  // --- Build hierarchical manufacturer list ---
  // A "parent group" = manufacturer with children. "Standalone" = no parent, no children.
  // Child brands that have a parent are NOT shown as standalone entries.
  interface ManufacturerGroup {
    parent: CatalogManufacturer;
    children: CatalogManufacturer[];
    aggregateCount: number;
  }

  const { manufacturerGroups, standaloneManufacturers, searchMatchedParentIds } = useMemo(() => {
    const lower = brandSearch.trim().toLowerCase();

    // Separate parents (have children) from standalones and children
    const parentMap = new Map<string, CatalogManufacturer>();
    const childIds = new Set<string>();

    // Identify parents — a manufacturer is a parent if other manufacturers reference it
    for (const m of manufacturers) {
      if (m.childManufacturers && m.childManufacturers.length > 0) {
        parentMap.set(m.id, m);
        for (const child of m.childManufacturers) {
          childIds.add(child.id);
        }
      }
    }

    // Build groups
    const groups: ManufacturerGroup[] = [];
    const standalone: CatalogManufacturer[] = [];
    const matchedParentIds = new Set<string>();

    for (const m of manufacturers) {
      if (childIds.has(m.id)) {
        // This is a child — it'll appear under its parent group
        continue;
      }

      if (parentMap.has(m.id)) {
        // This is a parent with children
        const childMfgs = manufacturers.filter(
          (c) => c.parentManufacturerId === m.id
        );
        const aggregateCount = m.machineCount + childMfgs.reduce((sum, c) => sum + c.machineCount, 0);

        // Brand search filter
        if (lower) {
          const parentMatches = m.name.toLowerCase().includes(lower);
          const anyChildMatches = childMfgs.some((c) => c.name.toLowerCase().includes(lower));
          if (!parentMatches && !anyChildMatches) continue;
          if (parentMatches || anyChildMatches) {
            matchedParentIds.add(m.id);
          }
        }

        groups.push({
          parent: m,
          children: childMfgs.sort((a, b) => b.machineCount - a.machineCount),
          aggregateCount,
        });
      } else {
        // Standalone manufacturer
        if (lower && !m.name.toLowerCase().includes(lower)) continue;
        standalone.push(m);
      }
    }

    // Sort groups and standalones by aggregate/machine count descending
    groups.sort((a, b) => b.aggregateCount - a.aggregateCount);
    standalone.sort((a, b) => b.machineCount - a.machineCount);

    return {
      manufacturerGroups: groups,
      standaloneManufacturers: standalone,
      searchMatchedParentIds: matchedParentIds,
    };
  }, [manufacturers, brandSearch]);

  // Auto-expand groups when brand search matches them
  useEffect(() => {
    if (searchMatchedParentIds.size > 0) {
      setExpandedParents((prev) => {
        const next = new Set(prev);
        searchMatchedParentIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [searchMatchedParentIds]);

  const toggleParentExpanded = useCallback((parentId: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }, []);

  // --- Active filter count ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (query.search) count++;
    if (query.manufacturerId) count++;
    if (query.minRpm || query.maxRpm) count++;
    if (query.minHp || query.maxHp) count++;
    if (query.taperTypeId) count++;
    if (query.controlSystemBrandId) count++;
    if (query.operationTypeId) count++;
    if (query.oemReady) count++;
    if (query.machineIdIntegrated) count++;
    return count;
  }, [query]);

  // --- Handlers ---
  const handleCategorySelect = useCallback(
    (categoryCode: string | undefined) => {
      setQuery((prev) => ({
        ...prev,
        categoryCode,
        manufacturerId: undefined,
        page: 1,
      }));
      setBrandSearch("");
    },
    []
  );

  const handleManufacturerFilter = useCallback(
    (manufacturerId: string | undefined) => {
      setQuery((prev) => ({ ...prev, manufacturerId, page: 1 }));
    },
    []
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        setQuery((prev) => ({ ...prev, search: undefined, page: 1 }));
        return;
      }
      if (value.trim().length < 2) return;
      debounceRef.current = setTimeout(() => {
        setQuery((prev) => ({ ...prev, search: value.trim(), page: 1 }));
      }, 300);
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const trimmed = searchValue.trim();
        setQuery((prev) => ({
          ...prev,
          search: trimmed.length >= 2 ? trimmed : undefined,
          page: 1,
        }));
      }
    },
    [searchValue]
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    setQuery((prev) => ({ ...prev, search: undefined, page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setBrandSearch("");
    setExpandedParents(new Set());
    setQuery((prev) => ({
      ...prev,
      search: undefined,
      manufacturerId: undefined,
      minRpm: undefined,
      maxRpm: undefined,
      minHp: undefined,
      maxHp: undefined,
      taperTypeId: undefined,
      controlSystemBrandId: undefined,
      operationTypeId: undefined,
      oemReady: undefined,
      machineIdIntegrated: undefined,
      page: 1,
    }));
  }, []);

  const handleToggleOemReady = useCallback(() => {
    setQuery((prev) => ({ ...prev, oemReady: prev.oemReady ? undefined : true, page: 1 }));
  }, []);

  // DR-2: omit-when-unchecked (NEVER send false) — same pattern as oemReady.
  const handleToggleMachineIdIntegrated = useCallback(() => {
    setQuery((prev) => ({
      ...prev,
      machineIdIntegrated: prev.machineIdIntegrated ? undefined : true,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setQuery((prev) => ({ ...prev, page: newPage }));
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    []
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setQuery((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
    },
    []
  );

  const handleCardClick = useCallback(
    (model: any) => {
      navigate(`/machinery/catalog/${model.id}`);
    },
    [navigate]
  );

  // --- Results info ---
  const from = browseTotalCount === 0 ? 0 : (query.page - 1) * query.pageSize + 1;
  const to = Math.min(query.page * query.pageSize, browseTotalCount);

  // =====================================================================
  //  SHARED: renderFilterContent (sidebar & accordion)
  // =====================================================================

  const renderFilterContent = () => (
    <>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
          padding: "0 12px",
          height: "40px",
          transition: "all 0.25s ease",
          boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
          mb: "16px",
        }}
      >
        <SearchOutlined
          sx={{
            fontSize: "16px",
            color: searchFocused ? dt.blue : dt.textMuted,
            flexShrink: 0,
            transition: "color 0.2s",
          }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder={translate("searchCatalogModels")}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "13px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "8px",
          }}
        />
        {searchValue && (
          <CloseOutlined
            onClick={handleClearSearch}
            sx={{
              fontSize: "16px",
              color: dt.textMuted,
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": { color: dt.textSecondary },
            }}
          />
        )}
      </Box>

      {/* Manufacturer filter header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "12px",
          px: "4px",
        }}
      >
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            color: dt.textMuted,
            fontFamily: dt.font,
          }}
        >
          {translate("manufacturer")}
        </Box>
        {activeFilterCount > 0 && (
          <Box
            onClick={handleClearFilters}
            sx={{
              fontSize: "12px",
              color: dt.blue,
              cursor: "pointer",
              fontWeight: 500,
              fontFamily: dt.font,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("clearAll")}
          </Box>
        )}
      </Box>

      {/* Brand search */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          padding: "0 10px",
          height: "34px",
          mb: "8px",
        }}
      >
        <SearchOutlined sx={{ fontSize: "14px", color: dt.textMuted, flexShrink: 0 }} />
        <input
          type="text"
          value={brandSearch}
          onChange={(e) => setBrandSearch(e.target.value)}
          placeholder={translate("searchBrands")}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "12px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
            marginLeft: "6px",
          }}
        />
      </Box>

      {/* "All brands" filter item */}
      <FilterItem
        label={translate("allBrands")}
        count={browseTotalCount}
        isSelected={!query.manufacturerId}
        onClick={() => handleManufacturerFilter(undefined)}
      />

      {/* Hierarchical manufacturer filters */}
      <Box sx={{ maxHeight: "280px", overflowY: "auto" }}>
        {/* Parent groups with collapsible children */}
        {manufacturerGroups.map((group) => {
          const isExpanded = expandedParents.has(group.parent.id);
          const isParentSelected = query.manufacturerId === group.parent.id;
          const isAnyChildSelected = group.children.some(
            (c) => query.manufacturerId === c.id
          );

          return (
            <Box key={group.parent.id}>
              {/* Parent header row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {/* Expand/collapse chevron */}
                <Box
                  onClick={() => toggleParentExpanded(group.parent.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "22px",
                    height: "22px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#F1F5F9" },
                  }}
                >
                  {isExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: "16px", color: dt.textMuted }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: "16px", color: dt.textMuted }} />
                  )}
                </Box>
                {/* Parent filter item — click to filter ALL children */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FilterItem
                    label={group.parent.name}
                    count={group.aggregateCount}
                    isSelected={isParentSelected || isAnyChildSelected}
                    statusColor="#8E44AD"
                    onClick={() => handleManufacturerFilter(group.parent.id)}
                  />
                </Box>
              </Box>

              {/* Children (visible when expanded) */}
              {isExpanded && group.children.map((child) => (
                <FilterItem
                  key={child.id}
                  label={child.name}
                  count={child.machineCount}
                  isSelected={query.manufacturerId === child.id}
                  statusColor="#8E44AD"
                  indent
                  onClick={() => handleManufacturerFilter(child.id)}
                />
              ))}
            </Box>
          );
        })}

        {/* Standalone manufacturers (no parent, no children) */}
        {standaloneManufacturers.map((mf: CatalogManufacturer) => (
          <FilterItem
            key={mf.id}
            label={mf.name}
            count={mf.machineCount}
            isSelected={query.manufacturerId === mf.id}
            statusColor="#8E44AD"
            onClick={() => handleManufacturerFilter(mf.id)}
          />
        ))}
      </Box>

      {/* DR-1: MIDAS Readiness section */}
      <Box sx={{ mt: "20px" }}>
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            color: dt.textMuted,
            fontFamily: dt.font,
            mb: "8px",
            px: "4px",
          }}
        >
          {translate("midasReadiness")}
        </Box>
        <CheckboxFilterItem
          label={translate("machineIdIntegrated")}
          checked={!!query.machineIdIntegrated}
          onClick={handleToggleMachineIdIntegrated}
        />
        <CheckboxFilterItem
          label={translate("oemReady")}
          checked={!!query.oemReady}
          onClick={handleToggleOemReady}
          trailingIcon={<OemStar size={11} fill="#C9962E" />}
        />
      </Box>

      {/* Phase 7F: Spec-based filters */}
      <Box sx={{ mt: "20px" }}>
        {/* RPM Range */}
        <Box sx={{ mb: "16px" }}>
          <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font, mb: "8px", px: "4px" }}>
            {translate("spindleRpmRange")}
          </Box>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="number"
              placeholder={translate("min")}
              value={query.minRpm ?? ""}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value) : undefined;
                setQuery((prev) => ({ ...prev, minRpm: val, page: 1 }));
              }}
              style={{ border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "6px 10px", width: "100%", fontSize: "12px", fontFamily: 'var(--font-mono)', color: dt.textPrimary, background: dt.inputBg, outline: "none" }}
            />
            <Box sx={{ fontSize: "11px", color: dt.textMuted, flexShrink: 0 }}>&ndash;</Box>
            <input
              type="number"
              placeholder={translate("max")}
              value={query.maxRpm ?? ""}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value) : undefined;
                setQuery((prev) => ({ ...prev, maxRpm: val, page: 1 }));
              }}
              style={{ border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "6px 10px", width: "100%", fontSize: "12px", fontFamily: 'var(--font-mono)', color: dt.textPrimary, background: dt.inputBg, outline: "none" }}
            />
          </Box>
        </Box>

        {/* HP Range */}
        <Box sx={{ mb: "16px" }}>
          <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font, mb: "8px", px: "4px" }}>
            {translate("spindleHpRange")}
          </Box>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="number"
              placeholder={translate("min")}
              value={query.minHp ?? ""}
              onChange={(e) => {
                const val = e.target.value ? parseFloat(e.target.value) : undefined;
                setQuery((prev) => ({ ...prev, minHp: val, page: 1 }));
              }}
              style={{ border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "6px 10px", width: "100%", fontSize: "12px", fontFamily: 'var(--font-mono)', color: dt.textPrimary, background: dt.inputBg, outline: "none" }}
            />
            <Box sx={{ fontSize: "11px", color: dt.textMuted, flexShrink: 0 }}>&ndash;</Box>
            <input
              type="number"
              placeholder={translate("max")}
              value={query.maxHp ?? ""}
              onChange={(e) => {
                const val = e.target.value ? parseFloat(e.target.value) : undefined;
                setQuery((prev) => ({ ...prev, maxHp: val, page: 1 }));
              }}
              style={{ border: `1px solid ${dt.cardBorder}`, borderRadius: "6px", padding: "6px 10px", width: "100%", fontSize: "12px", fontFamily: 'var(--font-mono)', color: dt.textPrimary, background: dt.inputBg, outline: "none" }}
            />
          </Box>
        </Box>

        {/* Taper Type filter */}
        {taperTypes.length > 0 && (
          <Box sx={{ mb: "16px" }}>
            <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: dt.textMuted, fontFamily: dt.font, mb: "8px", px: "4px" }}>
              {translate("taperType")}
            </Box>
            <Box sx={{ maxHeight: "140px", overflowY: "auto" }}>
              <FilterItem
                label={translate("allTaperTypes")}
                count={browseTotalCount}
                isSelected={!query.taperTypeId}
                onClick={() => setQuery((prev) => ({ ...prev, taperTypeId: undefined, page: 1 }))}
              />
              {taperTypes.map((tt: TaperType) => (
                <FilterItem
                  key={tt.id}
                  label={getReferenceLabel("taper", tt)}
                  count={0}
                  isSelected={query.taperTypeId === tt.id}
                  statusColor="#8E44AD"
                  indent
                  onClick={() => setQuery((prev) => ({ ...prev, taperTypeId: tt.id, page: 1 }))}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflow: "hidden",
        px: { xs: "16px", md: "24px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: { xs: 2, md: 3 },
          pb: 1,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: isPhone ? "20px" : isCompact ? "22px" : "28px",
              fontWeight: 800,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.5px",
            }}
          >
            {translate("machineCatalog")}
          </Box>
          <Box
            sx={{
              width: "40px",
              height: "3px",
              background: `linear-gradient(90deg, ${dt.blue}, transparent)`,
              borderRadius: "2px",
              mt: 0.5,
            }}
          />
        </Box>

      </Box>

      {/* Category tabs */}
      <Box sx={{ py: 1.5 }}>
        <CatalogCategoryTabs
          categories={categories}
          selectedCode={query.categoryCode}
          totalCount={totalCategoryCount}
          onSelect={handleCategorySelect}
        />
      </Box>

      {/* Main content: sidebar + card grid */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Desktop sidebar */}
        {!isCompact && (
          <Box
            sx={{
              width: "260px",
              minWidth: "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              overflowY: "auto",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: "22px",
                boxShadow: dt.cardShadow,
              }}
            >
              {renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* Main area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflowY: "auto",
            pb: 3,
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.15)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.25)" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.15) transparent",
          }}
        >
          {/* Accordion filters (tablet & phone) */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                  }}
                >
                  {translate("filters")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Results count */}
          {!browseLoading && browseTotalCount > 0 && (
            <Box
              sx={{
                fontSize: "12px",
                fontFamily: dt.fontMono,
                fontWeight: 500,
                color: dt.textMuted,
              }}
            >
              {translate("showingResults", {
                from: from.toLocaleString(getUiLocale()),
                to: to.toLocaleString(getUiLocale()),
                total: browseTotalCount.toLocaleString(getUiLocale()),
              })}
            </Box>
          )}

          {/* Loading state */}
          {browseLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <CircularProgress size={36} sx={{ color: dt.blue }} />
            </Box>
          )}

          {/* Empty state */}
          {!browseLoading && browseTotalCount === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: dt.cardShadow,
                minHeight: "400px",
                flex: 1,
                gap: 2,
              }}
            >
              <PrecisionManufacturingOutlinedIcon
                sx={{ fontSize: 48, color: dt.textMuted }}
              />
              <Box
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                }}
              >
                {translate("noModelsMatchFilters")}
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                }}
              >
                {translate("noModelsMatchFiltersSecondary")}
              </Box>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  borderRadius: dt.radiusPill,
                  textTransform: "none",
                  fontFamily: dt.font,
                  fontWeight: 500,
                  fontSize: "13px",
                  borderColor: dt.blue,
                  color: dt.blue,
                  "&:hover": {
                    borderColor: dt.blueHover,
                    backgroundColor: dt.blueSoft,
                  },
                }}
              >
                {translate("clearFilters")}
              </Button>
            </Box>
          )}

          {/* Card grid */}
          {!browseLoading && browseTotalCount > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : isCompact
                    ? "repeat(auto-fill, minmax(300px, 1fr))"
                    : "repeat(auto-fill, minmax(320px, 1fr))",
                gap: isPhone ? "10px" : isCompact ? "14px" : "16px",
              }}
            >
              {browseResults.map((model, index) => (
                <CatalogModelCard
                  key={model.id}
                  model={model}
                  index={index}
                  onClick={handleCardClick}
                  onAddMachine={handleAddMachineFromCard}
                />
              ))}
            </Box>
          )}

          {/* Pagination */}
          {!browseLoading && browseTotalCount > 0 && (
            <Box
              sx={{
                mt: 2,
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                ...paginationWrapperSx,
              }}
            >
              <LightTablePagination
                totalCount={browseTotalCount}
                currentPage={query.page}
                pageSize={query.pageSize}
                pageSizeOptions={[20, 50, 100]}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageLabel={translate("rowsPerPage")}
                ofLabel={translate("of")}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* MachineDialog for adding machine from catalog card */}
      <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
        <MachineDialog
          key={`browse-add-${showAddDialog}-${addDialogModel?.id}`}
          open={showAddDialog}
          onClose={() => { setShowAddDialog(false); setAddDialogModel(null); }}
          isNew={true}
          preSelectedCatalogModel={addDialogModel || undefined}
          onCreated={(id) => navigate(id ? `/machinery?newMachine=${id}` : "/machinery")}
        />
      </PermissionGuard>
    </Box>
  );
};

export default CatalogBrowsePage;
